import api from './api';

// ============================================================================
// Types
// ============================================================================

export interface ProviderSchemaVersion {
  id: string;
  providerSource: string;
  version: string;
  extractedAt: string;
  resourceCount: number;
}

export interface TerraformAttributeSchema {
  type: any;
  description?: string;
  description_kind?: string;
  required?: boolean;
  optional?: boolean;
  computed?: boolean;
  sensitive?: boolean;
  deprecated?: boolean;
  nested_type?: {
    attributes: Record<string, TerraformAttributeSchema>;
    nesting_mode: 'single' | 'list' | 'set' | 'map';
    min_items?: number;
    max_items?: number;
  };
}

export interface TerraformBlockSchema {
  attributes?: Record<string, TerraformAttributeSchema>;
  block_types?: Record<string, {
    nesting_mode: 'single' | 'list' | 'set' | 'map';
    block: TerraformBlockSchema;
    min_items?: number;
    max_items?: number;
  }>;
  description?: string;
}

export interface TerraformResourceSchema {
  version: number;
  block: TerraformBlockSchema;
}

export interface ModuleSummary {
  name: string;
  path: string;
  variableCount: number;
  requiredCount: number;
  fetchedAt: string;
}

export interface ParsedVariableType {
  raw: string;
  base: string;
  element?: ParsedVariableType;
  attributes?: Record<string, {
    type: ParsedVariableType;
    optional?: boolean;
    default?: any;
  }>;
}

export interface ModuleVariable {
  name: string;
  type: ParsedVariableType;
  description: string;
  default?: any;
  required: boolean;
  sensitive?: boolean;
  nullable?: boolean;
  validation?: {
    condition: string;
    error_message: string;
  };
  allowedValues?: string[];
}

export interface GitLabModuleSource {
  type: 'gitlab';
  gitlabInstanceId: string;
  projectId: number;
  modulesPath: string;
  branch: string;
}

export interface UrlModuleSource {
  type: 'url';
  url: string;
}

export type ModuleRegistryConfig = GitLabModuleSource | UrlModuleSource;

export interface RegistryProvider {
  namespace: string;
  type: string;
  source: string;
}

export interface ProviderVersionInfo {
  version: string;
  protocols?: string[];
  platforms?: { os: string; arch: string }[];
}

// ============================================================================
// Template Types
// ============================================================================

export interface OptionsSource {
  type: 'rangeos' | 'provider-schema' | 'rangeos-swagger';
  endpointId?: string;
  resourceType?: string;
  labelField?: string;
  valueField?: string;
  schemaVersionId?: string;
  fieldPath?: string;
  swaggerSchema?: string;     // OpenAPI schema name (e.g. "VMSpecification")
  swaggerField?: string;      // field with enum values (e.g. "iconType")
}

export interface TemplateVariable {
  name: string;
  label: string;
  type: 'string' | 'number' | 'bool' | 'list' | 'map' | 'hcl';
  description?: string;
  default?: any;
  required: boolean;
  generatedFrom?: string; // if set, auto-copies value from the named variable
  options?: string[];     // if set, renders as dropdown with these allowed values
  optionsSource?: OptionsSource; // if set, options are fetched dynamically at render time
}

export interface TerraformTemplate {
  id: string;
  name: string;
  description?: string;
  hclTemplate: string;
  variables: TemplateVariable[];
  linkedModule?: string;
  sourceFile?: {
    gitlabInstanceId: string;
    projectId: number;
    branch: string;
    filePath: string;
  };
  createdAt: string;
  updatedAt: string;
}

export type TerraformTemplateSummary = Omit<TerraformTemplate, 'hclTemplate'>;

// ============================================================================
// API Client
// ============================================================================

export const terraformApi = {
  // Schema Versions
  listVersions: async (): Promise<ProviderSchemaVersion[]> => {
    const { data } = await api.get<ProviderSchemaVersion[]>('/terraform/versions');
    return data;
  },

  importSchema: async (schema: any, version?: string): Promise<ProviderSchemaVersion> => {
    const { data } = await api.post<{ success: boolean; data: ProviderSchemaVersion }>(
      '/terraform/versions/import',
      { schema, version },
    );
    return data.data;
  },

  importSchemaFromUrl: async (url: string, version?: string): Promise<ProviderSchemaVersion> => {
    const { data } = await api.post<{ success: boolean; data: ProviderSchemaVersion }>(
      '/terraform/versions/import',
      { url, version },
    );
    return data.data;
  },

  // Registry Discovery
  discoverProviders: async (registryHost: string, namespace?: string): Promise<RegistryProvider[]> => {
    const { data } = await api.post<{ registryHost: string; providers: RegistryProvider[] }>(
      '/terraform/registry/discover',
      { registryHost, namespace },
    );
    return data.providers;
  },

  listRegistryVersions: async (
    registryHost: string,
    namespace: string,
    type: string,
  ): Promise<ProviderVersionInfo[]> => {
    const { data } = await api.post<{ provider: string; versions: ProviderVersionInfo[] }>(
      '/terraform/registry/versions',
      { registryHost, namespace, type },
    );
    return data.versions;
  },

  extractFromRegistry: async (providerSource: string, version: string): Promise<ProviderSchemaVersion> => {
    const { data } = await api.post<{ success: boolean; data: ProviderSchemaVersion }>(
      '/terraform/registry/extract',
      { providerSource, version },
      { timeout: 180000 }, // 3 min — terraform init can be slow
    );
    return data.data;
  },

  deleteVersion: async (id: string): Promise<void> => {
    await api.delete(`/terraform/versions/${id}`);
  },

  listResourceTypes: async (versionId: string): Promise<string[]> => {
    const { data } = await api.get<string[]>(`/terraform/versions/${versionId}/resources`);
    return data;
  },

  getResourceSchema: async (versionId: string, resourceType: string): Promise<TerraformResourceSchema> => {
    const { data } = await api.get<TerraformResourceSchema>(
      `/terraform/versions/${versionId}/resources/${encodeURIComponent(resourceType)}`,
    );
    return data;
  },

  // Modules
  getModuleConfig: async (): Promise<ModuleRegistryConfig | null> => {
    const { data } = await api.get('/terraform/modules/config');
    return data && data.gitlabInstanceId ? data : null;
  },

  setModuleConfig: async (config: ModuleRegistryConfig): Promise<void> => {
    await api.post('/terraform/modules/configure', config, { timeout: 120000 });
  },

  listModules: async (): Promise<ModuleSummary[]> => {
    const { data } = await api.get<ModuleSummary[]>('/terraform/modules');
    return data;
  },

  refreshModules: async (): Promise<{ count: number }> => {
    const { data } = await api.post<{ success: boolean; count: number }>('/terraform/modules/refresh');
    return data;
  },

  getModuleVariables: async (moduleName: string): Promise<ModuleVariable[]> => {
    const { data } = await api.get<ModuleVariable[]>(`/terraform/modules/${encodeURIComponent(moduleName)}/variables`);
    return data;
  },

  // HCL Generation
  generateResourceHCL: async (
    resourceType: string,
    resourceName: string,
    attributes: Record<string, any>,
    schemaVersionId?: string,
  ): Promise<string> => {
    const { data } = await api.post<{ hcl: string }>('/terraform/generate-hcl', {
      resourceType, resourceName, attributes, schemaVersionId,
    });
    return data.hcl;
  },

  generateModuleHCL: async (
    moduleName: string,
    moduleSource: string,
    instanceName: string,
    variables: Record<string, any>,
  ): Promise<string> => {
    const { data } = await api.post<{ hcl: string }>('/terraform/generate-module-hcl', {
      moduleName, moduleSource, instanceName, variables,
    });
    return data.hcl;
  },

  // Templates
  listTemplates: async (): Promise<TerraformTemplateSummary[]> => {
    const { data } = await api.get<TerraformTemplateSummary[]>('/terraform/templates');
    return data;
  },

  createTemplate: async (template: {
    name: string;
    description?: string;
    hclTemplate: string;
    variables: TemplateVariable[];
    linkedModule?: string;
    sourceFile?: TerraformTemplate['sourceFile'];
  }): Promise<TerraformTemplate> => {
    const { data } = await api.post<TerraformTemplate>('/terraform/templates', template);
    return data;
  },

  getTemplate: async (id: string): Promise<TerraformTemplate> => {
    const { data } = await api.get<TerraformTemplate>(`/terraform/templates/${id}`);
    return data;
  },

  updateTemplate: async (id: string, template: Partial<{
    name: string;
    description: string;
    hclTemplate: string;
    variables: TemplateVariable[];
    linkedModule: string;
    sourceFile: TerraformTemplate['sourceFile'];
  }>): Promise<TerraformTemplate> => {
    const { data } = await api.put<TerraformTemplate>(`/terraform/templates/${id}`, template);
    return data;
  },

  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/terraform/templates/${id}`);
  },

  renderTemplate: async (id: string, values: Record<string, any>): Promise<string> => {
    const { data } = await api.post<{ hcl: string }>(`/terraform/templates/${id}/render`, { values });
    return data.hcl;
  },

  convertHclToTemplate: async (hclContent: string): Promise<{ hclTemplate: string; variables: TemplateVariable[] }> => {
    const { data } = await api.post<{ hclTemplate: string; variables: TemplateVariable[] }>(
      '/terraform/templates/convert',
      { hclContent },
    );
    return data;
  },

  listSwaggerSchemas: async (endpointId: string): Promise<string[]> => {
    const { data } = await api.get<string[]>(`/terraform/swagger-schemas/${endpointId}`);
    return data;
  },

  listSwaggerEnumFields: async (endpointId: string, schemaName: string): Promise<Array<{ field: string; values: string[] }>> => {
    const { data } = await api.get<Array<{ field: string; values: string[] }>>(
      `/terraform/swagger-schemas/${endpointId}/${encodeURIComponent(schemaName)}/enums`,
    );
    return data;
  },

  resolveOptions: async (optionsSource: OptionsSource): Promise<{ options: Array<{ label: string; value: string }>; fieldDescription?: string; message?: string }> => {
    const { data } = await api.post<{ options: Array<{ label: string; value: string }>; fieldDescription?: string; message?: string }>(
      '/terraform/templates/resolve-options',
      { optionsSource },
    );
    return data;
  },
};
